/**
 * O cerco de uma tela que quebrou.
 *
 * <p><b>O que ele evita.</b> Sem cerco, um erro de renderização em qualquer página
 * desmonta a árvore inteira: o React tira tudo do ar, e o que sobra é uma janela branca —
 * sem barra lateral, sem cabeçalho, sem caminho de volta. A pessoa não tem nem como ir
 * para outra tela; só recarregando, e recarregando cai na mesma página quebrada.
 *
 * <p>Não é hipótese. Um payload malformado — um campo que virou nulo depois de um deploy,
 * uma lista que veio como objeto — já produz isso hoje, e o sintoma que chega é "o GG
 * Pilot sumiu", que não diz nada sobre onde procurar.
 *
 * <p><b>Onde ele fica.</b> Em volta do {@code <Outlet />}, e não em volta do app: assim a
 * barra lateral e o cabeçalho continuam de pé, e a tela quebrada vira um retângulo com
 * explicação no meio do produto. Trocar de página é clicar ao lado.
 *
 * <p><b>O que ele não faz.</b> Não tenta se recuperar sozinho nem esconder o problema. O
 * erro vai para o console inteiro, e a tela mostra a mensagem — porque quem está usando é
 * o time que constrói isto, e "algo deu errado" sem dizer o quê custa uma ida ao console
 * de quem já estava olhando para a tela.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  /** Muda quando a rota muda: é o que faz o cerco se reerguer ao navegar. */
  rota: string;
  children: ReactNode;
}

interface Estado {
  erro: Error | null;
}

export class TelaQueQuebrou extends Component<Props, Estado> {
  state: Estado = { erro: null };

  static getDerivedStateFromError(erro: Error): Estado {
    return { erro };
  }

  /**
   * Navegar limpa o erro.
   *
   * <p>Sem isto, o cerco fica preso: uma vez quebrado, ele continuaria mostrando a
   * mensagem mesmo depois de a pessoa clicar em outra tela — porque o estado de erro é
   * dele, não da rota. É o modo clássico de um error boundary transformar um problema
   * de uma página num problema do app inteiro, que é exatamente o que ele existe para
   * evitar.
   */
  componentDidUpdate(anterior: Props) {
    if (anterior.rota !== this.props.rota && this.state.erro) {
      this.setState({ erro: null });
    }
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    // Console, e não um serviço: não há coletor de erro neste projeto, e inventar um
    // aqui seria decidir sozinho que o produto manda dados para fora.
    console.error('A tela quebrou:', erro, info.componentStack);
  }

  render() {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    return (
      <div className="quebrou">
        <b>Esta tela quebrou</b>
        <p>
          O resto do GG Pilot continua funcionando — use a barra lateral para ir a outra
          página. Se isto se repetir, o texto abaixo é o que o time precisa saber.
        </p>
        <code>{erro.message || String(erro)}</code>
      </div>
    );
  }
}
